/**
 * useQuotaBar — credits-era balance bar (PRI-437 P8 → PRI-496 public cutover).
 * Fetches the credit balance on mount; re-fetches when refreshKey bumps.
 * The input bar shows one number: seconds of processing credit remaining.
 *
 * PRI-496: reads the PUBLIC surface (dogfooding) —
 *   * balance:   GET /v1/credits  (sdk.credits.retrieve, limit 1 — the bar
 *                doesn't need transaction pages)
 *   * threshold: GET /v1/credit-pricing (sdk.creditPricing.retrieve) —
 *                auto_refill_threshold_seconds, the global config value.
 *                Pricing is static config, so it's module-cached; a failure
 *                just means no low-balance styling cue (threshold null).
 * Previously read the internal console endpoint /v1/billing/credits, which
 * carried the per-user auto-refill threshold. The public config default is
 * identical for every user who hasn't customized auto-refill — the value only
 * drives a styling cue, never billing math.
 */
import { useState, useEffect } from 'react'
import { sdk } from '../../core/lib/api/sdk'
import type { CreditPricing } from '@primate-intelligence/sdk/browser'
import type { QuotaBarData } from '../../core/components/v2/pv2-shared'

// Module-level pricing cache — static config, one read per page load.
let pricingPromise: Promise<CreditPricing> | null = null

function getCachedPricing(): Promise<CreditPricing> {
  if (!pricingPromise) {
    pricingPromise = sdk.creditPricing.retrieve().catch(err => {
      // Don't cache failures — allow a retry on the next mount/refresh.
      pricingPromise = null
      throw err
    })
  }
  return pricingPromise
}

/** Test hook: reset the module-level pricing cache between test cases. */
export function __clearPricingCacheForTests(): void {
  pricingPromise = null
}

/**
 * @param isSignedIn      Clerk session present.
 * @param refreshKey      bump to re-fetch.
 * @param anonTrialActive PRI-503: an anonymous free-trial key (pv_live_ free
 *                        grant) is stored — enables the bar for signed-OUT
 *                        users. The SDK bearer falls back to the anon key via
 *                        the token registry, so the same GET /v1/credits call
 *                        returns the trial balance.
 */
export function useQuotaBar(isSignedIn: boolean, refreshKey: number, anonTrialActive = false): QuotaBarData | null {
  const [data, setData] = useState<Omit<QuotaBarData, 'loading'> | null>(null)
  const [loading, setLoading] = useState(false)
  const enabled = isSignedIn || anonTrialActive

  useEffect(() => {
    if (!enabled) { setData(null); return }
    let cancelled = false
    setLoading(true)
    Promise.all([
      sdk.credits.retrieve({ limit: 1 }),
      getCachedPricing().catch(() => null),
    ])
      .then(([credits, pricing]) => {
        if (cancelled) return
        setData({
          balanceSeconds: credits.balance_seconds,
          thresholdSeconds: pricing?.auto_refill_threshold_seconds ?? null,
          anonTrial: !isSignedIn,
        })
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [enabled, isSignedIn, refreshKey])

  if (!enabled) return null
  return {
    ...(data ?? { balanceSeconds: null, thresholdSeconds: null, anonTrial: !isSignedIn }),
    loading,
  }
}
